import { House, HouseManager } from '@antification/core';
import { Camera } from './Camera';

/**
 * What a building looks like.
 *
 * Isometric boxes with windows, a door, a roof and — while it is going up —
 * scaffolding. Homes get a peak and a wooden door; corporations get a flat roof,
 * a lit sign and a grid of glass. `HouseManager` decides who lives where, who
 * owns what and which corporations still have subscribers; since ADR-008 it has
 * no opinion about any of it above the foundation.
 *
 * `houses` is still passed in because one thing on screen is a fact about the
 * economy rather than about the building: a corporation with no subscribers is
 * drawn dimmed, and only the manager knows the count.
 *
 * `shed` is the second such fact and it belongs to the grid: a building the
 * district could not power this cycle is drawn with its lights off. That is
 * Law 2 applied to a flow — *power shortage is houses going dark on someone's
 * screen* — and it is the only place in the game a brownout is visible, since
 * nothing above the district will ever be told about one.
 *
 * `parched` is the third, and it belongs to the mains. Every finished building
 * has a strip of watered planting around it; a building whose garden the
 * district could not water this cycle has a brown one instead. The design names
 * that trace itself — *"in drought, browned lawns"* — and it is the whole of
 * what a water shortage does. Nothing dies, nothing slows, nothing is charged.
 * The lawn is the consequence.
 */
export class HouseRenderer {
  render(
    ctx: CanvasRenderingContext2D,
    camera: Camera,
    houses: HouseManager,
    house: House,
    shed = false,
    parched = false
  ): void {
    const scale = camera.getScale();
    const progress = house.buildProgress;

    if (progress <= 0) return;

    // Calculate bounding box of footprint for rendering
    let minDx = 0, maxDx = 0, minDy = 0, maxDy = 0;
    for (const cell of house.footprint) {
      minDx = Math.min(minDx, cell.dx);
      maxDx = Math.max(maxDx, cell.dx);
      minDy = Math.min(minDy, cell.dy);
      maxDy = Math.max(maxDy, cell.dy);
    }

    const footprintWidth = maxDx - minDx + 1;
    const footprintHeight = maxDy - minDy + 1;

    // Get center of footprint in screen space
    const centerGridX = house.gridX + (maxDx + minDx) / 2;
    const centerGridY = house.gridY + (maxDy + minDy) / 2;
    const screen = camera.gridToScreen(centerGridX, centerGridY);
    const x = screen.x;
    const y = screen.y;

    // Dimensions in screen space
    const cellW = camera.cellWidth * scale;
    const cellH = camera.cellHeight * scale;
    const baseWidth = cellW * footprintWidth * 0.7;
    const baseDepth = cellH * footprintHeight * 0.7;
    const floorHeight = 12 * scale;
    const wallHeight = floorHeight * house.floors * progress;
    const roofHeight = 10 * scale * progress;

    ctx.save();

    // Dim dormant corporations (0 subscribers) and vacant homes
    if (house.built && house.occupant === 'corporation' && houses.getSubscriberCount(house.id) === 0) {
      ctx.globalAlpha = 0.45;
    } else if (house.vacant) {
      ctx.globalAlpha = 0.5;
    }

    const currentWallHeight = wallHeight;
    const currentRoofHeight = progress > 0.7 ? roofHeight * ((progress - 0.7) / 0.3) : 0;

    // The garden: a margin of planting between the building and its lot, laid
    // once the place is finished. Green while the district can water it, the
    // colour of the graded dirt it was laid on when it cannot. A site under
    // construction has none — there is nothing to landscape yet.
    if (house.built) {
      const gardenWidth = cellW * footprintWidth * 0.92;
      const gardenDepth = cellH * footprintHeight * 0.92;
      ctx.fillStyle = parched ? '#c2b280' : '#7fae5c';
      ctx.beginPath();
      ctx.moveTo(x, y - gardenDepth / 2);
      ctx.lineTo(x + gardenWidth / 2, y);
      ctx.lineTo(x, y + gardenDepth / 2);
      ctx.lineTo(x - gardenWidth / 2, y);
      ctx.closePath();
      ctx.fill();
    }

    // Foundation
    if (progress > 0.1) {
      ctx.fillStyle = '#888';
      ctx.beginPath();
      ctx.moveTo(x, y - baseDepth / 2);
      ctx.lineTo(x + baseWidth / 2, y);
      ctx.lineTo(x, y + baseDepth / 2);
      ctx.lineTo(x - baseWidth / 2, y);
      ctx.closePath();
      ctx.fill();
    }

    // Walls
    if (progress > 0.2) {
      // Left wall
      ctx.fillStyle = house.color;
      ctx.beginPath();
      ctx.moveTo(x - baseWidth / 2, y);
      ctx.lineTo(x, y + baseDepth / 2);
      ctx.lineTo(x, y + baseDepth / 2 - currentWallHeight);
      ctx.lineTo(x - baseWidth / 2, y - currentWallHeight);
      ctx.closePath();
      ctx.fill();

      // Right wall
      ctx.fillStyle = this.darkenColor(house.color, 0.85);
      ctx.beginPath();
      ctx.moveTo(x, y + baseDepth / 2);
      ctx.lineTo(x + baseWidth / 2, y);
      ctx.lineTo(x + baseWidth / 2, y - currentWallHeight);
      ctx.lineTo(x, y + baseDepth / 2 - currentWallHeight);
      ctx.closePath();
      ctx.fill();

      // Windows
      if (progress > 0.5) {
        if (house.occupant === 'corporation') {
          this.renderCorporateWindows(ctx, x, y, baseWidth, baseDepth, currentWallHeight, scale, house.floors, house.accentColor, shed);
        } else {
          this.renderWindows(ctx, x, y, baseWidth, baseDepth, currentWallHeight, scale, house.floors, shed);
        }
      }
    }

    // Roof
    if (currentRoofHeight > 0) {
      if (house.occupant === 'corporation') {
        // Flat roof for corporations
        ctx.fillStyle = house.roofColor;
        ctx.beginPath();
        ctx.moveTo(x - baseWidth / 2, y - currentWallHeight);
        ctx.lineTo(x, y - baseDepth / 2 - currentWallHeight);
        ctx.lineTo(x + baseWidth / 2, y - currentWallHeight);
        ctx.lineTo(x, y + baseDepth / 2 - currentWallHeight);
        ctx.closePath();
        ctx.fill();

        // Corporate sign on top
        if (house.built) {
          const signHeight = 8 * scale;
          const signWidth = baseWidth * 0.6;

          // Sign pole
          ctx.fillStyle = '#555';
          ctx.fillRect(x - 1 * scale, y - currentWallHeight - signHeight, 2 * scale, signHeight);

          // Sign board. Unlit when the grid could not power the premises: a dark
          // shop with a glowing sign would be the one thing on this screen that
          // was not measured.
          ctx.fillStyle = shed ? this.darkenColor(house.accentColor, 0.35) : house.accentColor;
          ctx.beginPath();
          ctx.roundRect(
            x - signWidth / 2,
            y - currentWallHeight - signHeight - 6 * scale,
            signWidth,
            5 * scale,
            2
          );
          ctx.fill();

          // Sign border
          ctx.strokeStyle = 'rgba(255,255,255,0.5)';
          ctx.lineWidth = 1;
          ctx.stroke();
        }
      } else {
        // Peaked roof for family homes
        const roofPeak = y - currentWallHeight - currentRoofHeight;

        // Left roof slope
        ctx.fillStyle = house.roofColor;
        ctx.beginPath();
        ctx.moveTo(x - baseWidth / 2 - 3 * scale, y - currentWallHeight);
        ctx.lineTo(x, roofPeak);
        ctx.lineTo(x, y + baseDepth / 2 - currentWallHeight);
        ctx.lineTo(x - baseWidth / 2, y - currentWallHeight);
        ctx.closePath();
        ctx.fill();

        // Right roof slope
        ctx.fillStyle = this.darkenColor(house.roofColor, 1.2);
        ctx.beginPath();
        ctx.moveTo(x, roofPeak);
        ctx.lineTo(x + baseWidth / 2 + 3 * scale, y - currentWallHeight);
        ctx.lineTo(x + baseWidth / 2, y - currentWallHeight);
        ctx.lineTo(x, y + baseDepth / 2 - currentWallHeight);
        ctx.closePath();
        ctx.fill();
      }
    }

    // Scaffolding while building
    if (!house.built) {
      ctx.strokeStyle = '#8b7355';
      ctx.lineWidth = 1;
      ctx.setLineDash([2, 2]);
      ctx.beginPath();
      ctx.moveTo(x - baseWidth / 2 - 5 * scale, y);
      ctx.lineTo(x - baseWidth / 2 - 5 * scale, y - currentWallHeight - 8 * scale);
      ctx.moveTo(x + baseWidth / 2 + 5 * scale, y);
      ctx.lineTo(x + baseWidth / 2 + 5 * scale, y - currentWallHeight - 8 * scale);
      ctx.stroke();
      ctx.setLineDash([]);
    }

    ctx.restore();
  }

  private renderWindows(
    ctx: CanvasRenderingContext2D,
    x: number,
    y: number,
    baseWidth: number,
    baseDepth: number,
    wallHeight: number,
    scale: number,
    floors: number,
    shed: boolean
  ): void {
    // Lit glass, and the same glass with nothing behind it.
    const leftGlass = shed ? 'rgba(58, 66, 74, 0.9)' : 'rgba(135, 180, 220, 0.85)';
    const rightGlass = shed ? 'rgba(44, 51, 58, 0.9)' : 'rgba(100, 140, 180, 0.85)';

    // Scale window size based on building size (smaller buildings = smaller windows)
    const sizeRatio = Math.min(1, baseWidth / (25 * scale));
    const windowW = 3.5 * scale * sizeRatio;
    const windowH = 4.5 * scale * sizeRatio;
    const floorHeight = wallHeight / floors;

    // Isometric ratio: the walls have a 2:1 width:depth screen ratio
    // Left wall goes from back (x-baseWidth/2, y) to front (x, y+baseDepth/2)
    // So direction is: (baseWidth/2, baseDepth/2), normalized slope = baseDepth/baseWidth
    const isoSlope = baseDepth / baseWidth;

    // Windows on each floor
    for (let floor = 0; floor < floors; floor++) {
      const floorY = y + baseDepth / 2 - (floor + 0.65) * floorHeight;

      // === LEFT WALL WINDOW ===
      // Position window in the middle of the left wall, shifted left to avoid door
      // Left wall center is between (x-baseWidth/2, y) and (x, y+baseDepth/2)
      const leftWallCenterX = x - baseWidth / 4;
      const leftWallCenterY = y + baseDepth / 4;

      // Shift window left (toward back of wall) to create gap from door
      const windowShift = baseWidth * 0.12;
      const leftWinX = leftWallCenterX - windowW / 2 - windowShift;
      const leftWinY = leftWallCenterY - (floor + 0.5) * floorHeight - windowShift * isoSlope;

      // Draw parallelogram following left wall angle (going down-right)
      ctx.fillStyle = leftGlass;
      ctx.beginPath();
      ctx.moveTo(leftWinX, leftWinY);
      ctx.lineTo(leftWinX + windowW, leftWinY + windowW * isoSlope);
      ctx.lineTo(leftWinX + windowW, leftWinY + windowW * isoSlope - windowH);
      ctx.lineTo(leftWinX, leftWinY - windowH);
      ctx.closePath();
      ctx.fill();

      ctx.strokeStyle = 'rgba(50, 50, 50, 0.5)';
      ctx.lineWidth = 0.5;
      ctx.stroke();

      // === RIGHT WALL WINDOW ===
      // Right wall goes from front (x, y+baseDepth/2) to back (x+baseWidth/2, y)
      // Direction is: (baseWidth/2, -baseDepth/2), slope = -baseDepth/baseWidth
      const rightWallCenterX = x + baseWidth / 4;
      const rightWallCenterY = y + baseDepth / 4;

      const rightWinX = rightWallCenterX - windowW / 2;
      const rightWinY = rightWallCenterY - (floor + 0.5) * floorHeight;

      // Draw parallelogram following right wall angle (going up-right)
      ctx.fillStyle = rightGlass;
      ctx.beginPath();
      ctx.moveTo(rightWinX, rightWinY);
      ctx.lineTo(rightWinX + windowW, rightWinY - windowW * isoSlope);
      ctx.lineTo(rightWinX + windowW, rightWinY - windowW * isoSlope - windowH);
      ctx.lineTo(rightWinX, rightWinY - windowH);
      ctx.closePath();
      ctx.fill();
      ctx.stroke();
    }

    // === DOOR on left wall (near front) ===
    const doorW = 4 * scale * sizeRatio;
    const doorH = 7 * scale * sizeRatio;

    // Position door near the front of the left wall, shifted slightly left from edge
    const doorShift = baseWidth * 0.06;
    const doorX = x - baseWidth / 6 - doorShift;
    const doorY = y + baseDepth / 3 - doorShift * isoSlope;

    ctx.fillStyle = '#5a4030';
    ctx.beginPath();
    ctx.moveTo(doorX, doorY);
    ctx.lineTo(doorX + doorW, doorY + doorW * isoSlope);
    ctx.lineTo(doorX + doorW, doorY + doorW * isoSlope - doorH);
    ctx.lineTo(doorX, doorY - doorH);
    ctx.closePath();
    ctx.fill();

    ctx.strokeStyle = '#3a2820';
    ctx.lineWidth = 1;
    ctx.stroke();

    // Door handle
    ctx.fillStyle = '#c0a080';
    ctx.beginPath();
    ctx.arc(doorX + doorW * 0.75, doorY + doorW * isoSlope * 0.75 - doorH * 0.45, 1 * scale * sizeRatio, 0, Math.PI * 2);
    ctx.fill();
  }

  /**
   * Render grid-style windows for corporate buildings with proper isometric perspective.
   */
  private renderCorporateWindows(
    ctx: CanvasRenderingContext2D,
    x: number,
    y: number,
    baseWidth: number,
    baseDepth: number,
    wallHeight: number,
    scale: number,
    floors: number,
    accentColor: string,
    shed: boolean
  ): void {
    // Scale based on building size
    const sizeRatio = Math.min(1, baseWidth / (25 * scale));
    const windowSize = 3 * scale * sizeRatio;
    const windowGap = 1.5 * scale * sizeRatio;
    const floorHeight = wallHeight / floors;

    // Isometric slope ratio
    const isoSlope = baseDepth / baseWidth;

    // Helper to draw isometric window on left wall (slope goes down-right)
    const drawLeftWindow = (wx: number, wy: number, color: string) => {
      ctx.fillStyle = color;
      ctx.beginPath();
      ctx.moveTo(wx, wy);
      ctx.lineTo(wx + windowSize, wy + windowSize * isoSlope);
      ctx.lineTo(wx + windowSize, wy + windowSize * isoSlope - windowSize);
      ctx.lineTo(wx, wy - windowSize);
      ctx.closePath();
      ctx.fill();
      ctx.strokeStyle = 'rgba(40, 40, 40, 0.4)';
      ctx.lineWidth = 0.5;
      ctx.stroke();
    };

    // Helper to draw isometric window on right wall (slope goes up-right)
    const drawRightWindow = (wx: number, wy: number, color: string) => {
      ctx.fillStyle = color;
      ctx.beginPath();
      ctx.moveTo(wx, wy);
      ctx.lineTo(wx + windowSize, wy - windowSize * isoSlope);
      ctx.lineTo(wx + windowSize, wy - windowSize * isoSlope - windowSize);
      ctx.lineTo(wx, wy - windowSize);
      ctx.closePath();
      ctx.fill();
      ctx.strokeStyle = 'rgba(40, 40, 40, 0.4)';
      ctx.lineWidth = 0.5;
      ctx.stroke();
    };

    // Determine window count based on building size (fewer on small buildings)
    const isSmallBuilding = baseWidth < 20 * scale;
    const leftWallWindowCount = isSmallBuilding ? 2 : 3;
    const rightWallWindowCount = isSmallBuilding ? 1 : 2;

    // Grid of windows on each floor
    for (let floor = 0; floor < floors; floor++) {
      const isTopFloor = floor === floors - 1;
      const isGroundFloor = floor === 0;
      const litColor = isTopFloor ? accentColor : 'rgba(180, 210, 240, 0.9)';
      const litColorDark = isTopFloor ? accentColor : 'rgba(140, 170, 200, 0.9)';
      const windowColor = shed
        ? (isTopFloor ? this.darkenColor(accentColor, 0.35) : 'rgba(62, 70, 78, 0.9)')
        : litColor;
      const windowColorDark = shed
        ? (isTopFloor ? this.darkenColor(accentColor, 0.35) : 'rgba(48, 55, 62, 0.9)')
        : litColorDark;

      // Left wall windows - positioned along the wall diagonal
      // Shift windows left to make room for door on ground floor
      const windowShift = baseWidth * 0.15;
      const leftWallBaseX = x - baseWidth / 4 - windowShift;
      const leftWallBaseY = y + baseDepth / 4 - (floor + 0.5) * floorHeight - windowShift * isoSlope;

      // Fewer windows on ground floor of small buildings (door takes space)
      const leftWindowsThisFloor = (isGroundFloor && isSmallBuilding) ? 1 : leftWallWindowCount;
      const leftWindowOffset = (leftWallWindowCount - leftWindowsThisFloor) * 0.5; // Center remaining windows

      for (let w = 0; w < leftWindowsThisFloor; w++) {
        const offset = (w - (leftWindowsThisFloor - 1) / 2) * (windowSize + windowGap);
        const wx = leftWallBaseX + offset * 0.8;
        const wy = leftWallBaseY + offset * 0.8 * isoSlope;
        drawLeftWindow(wx, wy, windowColor);
      }

      // Right wall windows
      const rightWallBaseX = x + baseWidth / 4;
      const rightWallBaseY = y + baseDepth / 4 - (floor + 0.5) * floorHeight;

      for (let w = 0; w < rightWallWindowCount; w++) {
        const offset = (w - (rightWallWindowCount - 1) / 2) * (windowSize + windowGap);
        const wx = rightWallBaseX + offset * 0.8;
        const wy = rightWallBaseY - offset * 0.8 * isoSlope;
        drawRightWindow(wx, wy, windowColorDark);
      }
    }

    // Glass entrance door on left wall (near front), shifted left from edge
    const doorW = 5 * scale * sizeRatio;
    const doorH = 10 * scale * sizeRatio;
    const doorShift = baseWidth * 0.08;
    const doorX = x - baseWidth / 6 - doorShift;
    const doorY = y + baseDepth / 3 - doorShift * isoSlope;

    ctx.fillStyle = 'rgba(150, 200, 230, 0.9)';
    ctx.beginPath();
    ctx.moveTo(doorX, doorY);
    ctx.lineTo(doorX + doorW, doorY + doorW * isoSlope);
    ctx.lineTo(doorX + doorW, doorY + doorW * isoSlope - doorH);
    ctx.lineTo(doorX, doorY - doorH);
    ctx.closePath();
    ctx.fill();

    // Door frame with accent color
    ctx.strokeStyle = accentColor;
    ctx.lineWidth = 2;
    ctx.stroke();

    // Glass door divider
    ctx.strokeStyle = 'rgba(80, 80, 80, 0.5)';
    ctx.lineWidth = 1;
    const midX = doorX + doorW * 0.5;
    const midDy = doorW * isoSlope * 0.5;
    ctx.beginPath();
    ctx.moveTo(midX, doorY + midDy);
    ctx.lineTo(midX, doorY + midDy - doorH);
    ctx.stroke();
  }

  private darkenColor(color: string, factor: number): string {
    if (color.startsWith('#')) {
      const r = parseInt(color.slice(1, 3), 16);
      const g = parseInt(color.slice(3, 5), 16);
      const b = parseInt(color.slice(5, 7), 16);
      return `rgb(${Math.min(255, Math.floor(r * factor))}, ${Math.min(255, Math.floor(g * factor))}, ${Math.min(255, Math.floor(b * factor))})`;
    }
    return color;
  }
}
